import React, {useEffect, useMemo, useState} from 'react';
import {
    Form, Row, Col, Button,
} from 'antd';
import { getAirportOptions } from "../../FlightSearch/helper";
import translations from "../../../screens/Search/translations";
import StyledDatePicker from "../../CustomAntdComponents/StyledDatePickerB2C/DatePicker";
import {getDisabledDateRange, getExtraData, toSafeDate} from "../../../utils/helpers";
import {useI18nContext} from "../../../utils/I18n";
import {useStore} from "../../../store/global/appStore";
import {
    AirportFieldsWrapper,
    DepartureWrapper,
    SwitcherWrapper,
} from "../../FlightSearch/styles";
import StyledRadioGroup from "../../CustomAntdComponents/StyledRadioGroup";
import {ReactComponent as DirectionIcon} from "../../../assets/images/direction-hr.svg";
import {tripModesMultiCity} from "../../../screens/Search/constant";
import {Moment} from "moment";
import {FlightSearchStore} from "../../../store/flightSearch/types";
import { MultiDestinationsFlightSearchForm } from '../../FlightSearch/MultiDestinations';
import AirportSelector from "../../RouteSelectorB2C";
import PassengerCountSelector from '../../PassegerSelectorB2C';
import { DateRange } from 'react-day-picker';
import { Magnifier } from '@solar-icons/react';
import { BackButton, Header, Title } from '../../FlightSearch/MultiDestinations/styles';
import { LeftOutlined } from '@ant-design/icons';

type Props = {
    onSubmit: (arg?: any) => void;
    payload: FlightSearchStore;
    onChange: (arg) => void;
    onModifyChange?: (value: boolean) => void;
}
const FlightSearchDesktop = ({ onSubmit, onChange, payload, onModifyChange }: Props) => {
    const [form] = Form.useForm();
    const [showMultiCitySearch, setShowMultiCitySearch] = useState(false);
    const [selectedRange, setSelectedRange] = useState<DateRange | undefined>();
    const {
        state: { t },
    } = useI18nContext();
    const { airports } = useStore();

    const {
        FlightClass,
        DepartureDate,
        ReturnDate,
        numberOfAdults,
        DestinationLocationCode,
        OriginLocationCode,
        numberOfInfants,
        numberOfChildren,
        tripType,
        multiCity,
    } = payload;

    const tripTypeWatch = Form.useWatch('tripType', form);
    // const currentTripType = tripTypeWatch ?? tripType;

    // const tripModeRadioValue =
    //     currentTripType === "multi_city" ? "multi_city" : "one_way_round_trip";

    const handleTripModeChange = (e: any) => {
        const value = e.target.value;

        if (value === "multi_city") {
            onChange({ tripType: "multi_city" });
            form.setFieldsValue({ tripType: "multi_city" });
            return;
        }

        const departureInput = (DepartureDate as any)?.DepartureDate ?? DepartureDate;
        const isRangeDate = departureInput?.from && typeof departureInput.from !== "function";
        const hasReturnDate =
            Boolean((departureInput as any)?.to && typeof (departureInput as any).to !== "function") ||
            Boolean((departureInput as any)?._i?.to) ||
            (!isRangeDate && Boolean(ReturnDate));

        const nextTripType = hasReturnDate ? "round_trip" : "one_way";

        onChange({ tripType: nextTripType });
        form.setFieldsValue({ tripType: nextTripType });
    };

    const isRoundTrip = tripTypeWatch === 'round_trip';
    const currentTripType = tripTypeWatch ?? tripType;
    const isMultiCity = currentTripType === "multi_city";
    
    const tripModeRadioValue =
  currentTripType === "multi_city" ? "multi_city" : "one_way_round_trip";

    const formInitialValues = {
        FlightClass,
        DepartureDate,
        ReturnDate,
        FlightDateRange: [DepartureDate, ReturnDate],
        tripType,
        numberOfAdults,
        DestinationLocationCode,
        OriginLocationCode,
        numberOfInfants,
        numberOfChildren,
        multiCity,
    };

    const allAirports = [...airports]
    const airportOptions = useMemo(() => getAirportOptions(allAirports), [allAirports]);

    const validateDestination = (_: any, value: any) => {
        if (!value) {
            return Promise.reject(new Error(t(translations).validations.destination));
        }

        if (form.getFieldValue('OriginLocationCode') === value) {
            return Promise.reject(new Error(t(translations).validations.invalidDestination));
        }

        return Promise.resolve();
    };

    const onFormInputChange = (value: any, name: string) => {
        onChange({
            [name]: value,
        });

        form.setFieldsValue({
            [name]: value,
        });
    };

    const handleSwitchAirports = () => {
        const tempOrigin = form.getFieldValue('OriginLocationCode');
        const tempDestination = form.getFieldValue('DestinationLocationCode');

        onChange({
            OriginLocationCode: tempDestination,
            DestinationLocationCode: tempOrigin,
        });

        form.setFieldsValue({
            OriginLocationCode: tempDestination,
            DestinationLocationCode: tempOrigin,
        });
    }

    const onDateRangeChange = (values: [Moment, Moment]) => {
        let data = {};
        if(!values) {
            data = {DepartureDate: "", ReturnDate: "" }
        } else {
            data = { DepartureDate: values[0], ReturnDate: values[1] }
        }

        onChange(data);
        form.setFieldsValue(data);
    }


    const OriginLocationCodeWatch = Form.useWatch('OriginLocationCode', form);
    const DestinationLocationCodeWatch = Form.useWatch('DestinationLocationCode', form);

    useEffect(() => {
        const origin = allAirports.find((airport) => airport.Code === OriginLocationCodeWatch)
        const destination = allAirports.find((airport) => airport.Code === DestinationLocationCodeWatch)

        if(!origin || !destination) return;
        const isDomestic =  origin?.CountryCode === destination?.CountryCode;

        onChange({
            isDomestic,
        });

    }, [DestinationLocationCodeWatch, OriginLocationCodeWatch, tripTypeWatch]);

    const onFinish = (values) => {
        onSubmit(values.trips);
    };

    const extraData = getExtraData();

    const flightModes = tripModesMultiCity(t(translations));
    const MultiCityMaxRoute = extraData.MultiCityMaxRoute ? extraData.MultiCityMaxRoute : 5;

    const getInitialDateRange = (): DateRange | undefined => {
        const departureInput = (DepartureDate as any)?.DepartureDate ?? DepartureDate;
        const isRangeDate = departureInput?.from && typeof departureInput.from !== "function";
        const returnInput = (DepartureDate as any)?.ReturnDate ?? ReturnDate;

        const from = toSafeDate(departureInput);

        const to =
            toSafeDate((departureInput as any)?.to) ||
            toSafeDate((departureInput as any)?._i?.to) ||
            (!isRangeDate ? toSafeDate(returnInput) : undefined);

        return from ? { from, to } : undefined;
        };
    
    const initialDateRange = getInitialDateRange();
    

    return (
        <Form
            wrapperCol={{ span: 24 }}
            layout="horizontal"
            initialValues={formInitialValues}
            form={form}
            size="large"
            onFinish={onFinish}
        >

            <Row gutter={[8, 0]}>
                {isMultiCity && (
                    <Header>
                        <BackButton type="button" onClick={() => onModifyChange?.(false)} aria-label="Close multi city search">
                        <LeftOutlined />
                        </BackButton>
                        <Title>Multi city search</Title>
                    </Header>
                )}
                <Col span={24}>
                    <Form.Item>
                        <StyledRadioGroup
                            options={flightModes}
                            value={tripModeRadioValue}
                            onChange={handleTripModeChange}
                        />
                    </Form.Item>
                </Col>
                {isMultiCity && <Col span={8}>
                    <PassengerCountSelector onChange={onChange} payload={payload} />
                </Col>}

                {isMultiCity ? (
                    <Col span={18}>
                        <MultiDestinationsFlightSearchForm
                            airportOptions={airportOptions}
                            form={form}
                            limitSuggestionHeight
                            MultiCityMaxRoute={MultiCityMaxRoute}
                            onClose={() => onModifyChange?.(false)}
                            OriginLocationCode={OriginLocationCode}
                            DestinationLocationCode={DestinationLocationCode}
                            DepartureDate={DepartureDate}
                        />
                    </Col>)
                         :
                    (
                        <>
                            <Col span={10}>
                                <AirportFieldsWrapper>
                                    <Row gutter={[10, 0]}>
                                        <Col span={12}>
                                            <DepartureWrapper>
                                                <Form.Item
                                                    name="OriginLocationCode"
                                                    rules={[{ required: true, message: t(translations).validations.departure }]}
                                                >
                                                    <AirportSelector
                                                        label={t(translations).inputs.fromLabel}
                                                        placeholder={t(translations).inputs.fromPlaceholder}
                                                        value={OriginLocationCode}
                                                        options={airportOptions}
                                                        icon="takeoff"
                                                        tripType="flight"
                                                        limitSuggestionHeight
                                                        onChange={(value) => onFormInputChange(value, 'OriginLocationCode')}
                                                        mode="from"
                                                    />
                                                </Form.Item>
                                                <SwitcherWrapper onClick={handleSwitchAirports}>
                                                    <DirectionIcon />
                                                </SwitcherWrapper>
                                            </DepartureWrapper>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="DestinationLocationCode"
                                                rules={[
                                                    {
                                                        validator: validateDestination,
                                                    },
                                                ]}
                                            >
                                                <AirportSelector
                                                    label={t(translations).inputs.toLabel}
                                                    placeholder={t(translations).inputs.toPlaceholder}
                                                    value={DestinationLocationCode}
                                                    options={airportOptions}
                                                    icon="land"
                                                    tripType="flight"
                                                    limitSuggestionHeight
                                                    onChange={(value) => onFormInputChange(value, 'DestinationLocationCode')}
                                                    mode="to"
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </AirportFieldsWrapper>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                            name="DepartureDate"
                                            rules={[{ required: true, message: t(translations).validations.departureDate }]}
                                        >
                                            <StyledDatePicker
                                                label={t(translations).inputs.departureDate}
                                                value={DepartureDate}
                                                returnValue={ReturnDate}
                                                selectedRange={initialDateRange}
                                                disabledDate={getDisabledDateRange}
                                                    onChange={(value) => onFormInputChange(value, 'DepartureDate')}
                                                    setSelectedRange={(range) => {
                                                        setSelectedRange(range);

                                                        const nextValues = {
                                                            DepartureDate: range
                                                            ? {
                                                                from: range.from,
                                                                to: range.to,
                                                                }
                                                            : undefined,
                                                            ReturnDate: range?.to,
                                                            tripType: range?.to ? "round_trip" : "one_way",
                                                        };

                                                        onChange(nextValues);
                                                        form.setFieldsValue(nextValues);
                                                    }}
                                                />
                                                
                                        </Form.Item>
                            </Col>
                        </>
                    )}
                { !isMultiCity && (
                    <Col span={8}>
                        <PassengerCountSelector onChange={onChange} payload={payload} />
                    </Col>
                )}
                <Col
                    span={6}
                    offset={18}
                >
                    <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        block
                        className="search-button"
                    >
                        <Magnifier />
                        Search flights
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default FlightSearchDesktop;
